import { t } from '../i18n';
import { durationLabel } from './disappearing';

// Deleted accounts (M51, D169): the server flags them (`deleted: true` on a
// user, `senderDeleted: true` on a reply or chat preview) and sends its own
// English "Deleted account"; the app says it in the reader's language.

type Json = Record<string, unknown>;

/**
 * Rewrites, in place, the names of deleted accounts in an API response or
 * socket payload, and returns it. Anything else is left alone.
 */
export function localizeDeletedAccounts<T>(
  data: T,
  name: string = t('account.deletedAccount')
): T {
  const seen = new Set<unknown>();
  const visit = (value: unknown) => {
    if (!value || typeof value !== 'object' || seen.has(value)) return;
    seen.add(value);
    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }
    const object = value as Json;
    if (object.deleted === true && typeof object.displayName === 'string') {
      object.displayName = name;
    }
    if (
      object.senderDeleted === true &&
      typeof object.senderName === 'string'
    ) {
      object.senderName = name;
    }
    Object.values(object).forEach(visit);
  };
  visit(data);
  return data;
}

// Group info lines (M63, D234), by what changed.
const GROUP_EVENTS = {
  group_picture: 'chat.system.groupPicture',
  group_picture_removed: 'chat.system.groupPictureRemoved',
  group_description: 'chat.system.groupDescription',
} as const;

/**
 * A system line in the reader's language: the hand-over note (D171) and the
 * group info lines (D234); anything else is the server's text.
 */
export function systemMessageText(message: {
  content: string;
  metadata?: Record<string, unknown> | null;
  /** Who made the change, for group info lines. */
  sender?: { displayName: string } | null;
  senderName?: string;
}): string {
  const metadata = message.metadata;
  if (
    metadata?.event === 'root_handover' &&
    typeof metadata.newRootName === 'string'
  ) {
    return t('chat.system.rootHandover', { name: metadata.newRootName });
  }
  const who = message.sender?.displayName ?? message.senderName;
  if (who && typeof metadata?.event === 'string') {
    if (
      metadata.event === 'group_renamed' &&
      typeof metadata.name === 'string'
    ) {
      return t('chat.system.groupRenamed', { name: who, group: metadata.name });
    }
    const key = GROUP_EVENTS[metadata.event as keyof typeof GROUP_EVENTS];
    if (key) return t(key, { name: who });
    // Disappearing messages turned on or off (D238).
    if (metadata.event === 'group_disappearing') {
      return typeof metadata.seconds === 'number' && metadata.seconds > 0
        ? t('chat.system.disappearingOn', {
            name: who,
            time: durationLabel(metadata.seconds),
          })
        : t('chat.system.disappearingOff', { name: who });
    }
  }
  return message.content;
}
